import { CannedQuestionName, QuestionType } from '../../../api/models/survey';
import { COPY_BILLING_CONTACT_INFO_FIELD } from '../../../containers/checkout/constants';
import { EPHEMERAL_FIELD_NAME } from '../../../containers/formContainers/connectCreateEphemeralField';
import {
    AttendeeSurvey,
    BUYER_ID,
    SurveyFormData,
    SurveyQuestion,
} from '../../../models/survey';

const isString = (variable: unknown): variable is string =>
    typeof variable === 'string';

const isObject = (variable: unknown): variable is Record<string, unknown> =>
    !!variable && typeof variable === 'object' && !Array.isArray(variable);

const isEmpty = (variable: unknown) => {
    if (isString(variable) || Array.isArray(variable)) {
        return variable.length === 0;
    }

    if (isObject(variable)) {
        return Object.keys(variable).length === 0;
    }

    return variable === undefined || variable === null;
};

const getTrimmedStringValue = <T>(variable: T): T =>
    isString(variable) ? (variable.trim() as unknown as T) : variable;

const getTrimmedStringPropsObject = <Data extends Record<string, unknown>>(
    data: Data,
): Data =>
    isObject(data)
        ? (Object.fromEntries(
              Object.entries(data).flatMap(([key, value]) => {
                  const trimmedValue = isObject(value)
                      ? getTrimmedStringPropsObject(value)
                      : getTrimmedStringValue(value);

                  return isEmpty(trimmedValue) ? [] : [[key, trimmedValue]];
              }),
          ) as Data)
        : getTrimmedStringValue(data);

/**
 * Actual function to remove the wrongly selected values
 */
const isHiddenConditionalQuestion = (
    question: SurveyQuestion,
    answers: Record<string, unknown>,
) => {
    if (!question.renderConditionTrigger) {
        return false;
    }

    const { parentQuestionId, choiceId: questionParentChoice } =
        question.renderConditionTrigger;
    const selectedParentChoice = answers[parentQuestionId];

    // Parent question answer might be an array of selected values (for instance
    // if the question is a checkbox field) or a string
    const isTriggerChoiceSelected = Array.isArray(selectedParentChoice)
        ? selectedParentChoice.some(
              (questionId) => questionId === questionParentChoice,
          )
        : selectedParentChoice === questionParentChoice;

    // If selected value doesn't have its parent selected, then the child
    // question is not displayed.
    return !isTriggerChoiceSelected;
};

const getDisplayedQuestionsAnswersForAttendee = (
    questions: Array<SurveyQuestion>,
    answers: Record<string, unknown>,
) => {
    const displayedQuestions = questions.filter(
        (q) => !isHiddenConditionalQuestion(q, answers),
    );

    const cleanedAnswers = Object.fromEntries(
        displayedQuestions.map(({ name }) => [name, answers[name]]),
    );

    const hasRemovedAnyAnswer =
        displayedQuestions.length < displayedQuestions.length;

    return {
        cleanedAnswers,
        hasRemovedAnyAnswer,
    };
};

const getDisplayedQuestionsAnswers = ({
    answers,
    attendeeSurveys,
    questionsForBuyer,
}: {
    answers: SurveyFormData;
    attendeeSurveys: Array<AttendeeSurvey>;
    questionsForBuyer: Array<SurveyQuestion>;
}): SurveyFormData => {
    const {
        cleanedAnswers: cleanedBuyerAnswers,
        hasRemovedAnyAnswer: hasRemovedAnyBuyerAnswer,
    } = getDisplayedQuestionsAnswersForAttendee(
        [
            ...questionsForBuyer,
            // In some circumstances (for instance, when the event is in Quebec)
            // we ask for additional tax questions even if the event is not set
            // up explicitly to ask for tax information. This is achieved by
            // rendering the tax registration id question in the form even if it
            // is not part of the survey. Since we artificially inject this field
            // in the form at the frontend level, we also have to artificially
            // inject it here so we keep the user answer for this field when
            // processing the form. Otherwise this field answer would be removed
            // as answers to any other unknown question.
            {
                choices: [],
                html: '',
                isOutOfInventory: false,
                name: CannedQuestionName.taxRegistrationId,
                renderConditionTrigger: null,
                text: '',
                type: QuestionType.text,
                required: false,
            },
        ],
        answers[BUYER_ID] as Record<string, unknown>,
    );

    const cleanResultsByAttendee = Object.fromEntries(
        attendeeSurveys.map(({ id: attendeeId, survey }) => [
            attendeeId,
            getDisplayedQuestionsAnswersForAttendee(
                survey,
                answers[attendeeId] as Record<string, unknown>,
            ),
        ]),
    );

    const cleanedAnswersByAttendee = Object.fromEntries(
        Object.entries(cleanResultsByAttendee).map(
            ([attendeeId, { cleanedAnswers }]) => [attendeeId, cleanedAnswers],
        ),
    );
    const hasRemovedAnyAttendeeAnswer = Object.entries(
        cleanResultsByAttendee,
    ).some(([_attendeeId, { hasRemovedAnyAnswer }]) => hasRemovedAnyAnswer);

    const hasCleanedAnyAnswer =
        hasRemovedAnyBuyerAnswer || hasRemovedAnyAttendeeAnswer;

    const cleanedAnswers = {
        [BUYER_ID]: cleanedBuyerAnswers,
        ...cleanedAnswersByAttendee,
    };

    return hasCleanedAnyAnswer
        ? // We have to recursively clean the answers again when we remove any
          // wrong answer because removing one answer might affect some
          // conditional question that depends on the removed answer
          getDisplayedQuestionsAnswers({
              answers: cleanedAnswers,
              attendeeSurveys,
              questionsForBuyer,
          })
        : cleanedAnswers;
};

const getAnswersWithoutFrontendOnlyQuestions = (formData: SurveyFormData) => {
    const {
        // In some cases the frontend will ask for the email a second time to
        // confirm that the email was properly typed. The field used to ask for
        // email the second time does not correspond to any survey question in
        // the order, so we cannot send it to the backend (the backend will
        // reject any request that has answers to questions that were not present
        // in the survey). Here we remove the answer to the email confirmation
        // field if it was present
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        [CannedQuestionName.confirmEmailAddress]: _confirmEmailAddress,
        // The "Copy information from billing info" checkbox is displayed to
        // ease users filling the form but this field is not part of the survey
        // so the backend will reject any request that includes answers to this
        // field
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        [COPY_BILLING_CONTACT_INFO_FIELD]: _copyBillingContactInfo,
        ...otherBuyerAnswers
    } = (formData[BUYER_ID] as Record<string, unknown>) ?? {};

    const {
        // In order to fix EB-228399 we have to introduce a fake field in the
        // form. The name of that field lives in `EPHEMERAL_FIELD_NAME` constant
        // and we must remove its answer before sending the form to the backend,
        // otherwise the backend will reject the request because the
        // `EPHEMERAL_FIELD_NAME` field is not part of the survey
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        [EPHEMERAL_FIELD_NAME]: _ephemeralField,
        ...rest
    } = formData;

    return {
        ...rest,
        [BUYER_ID]: otherBuyerAnswers,
    };
};

const getAnswersWithoutHiddenConditionalQuestions = ({
    attendeeSurveys,
    formData,
    questionsForBuyer,
}: {
    attendeeSurveys: Array<AttendeeSurvey>;
    formData: SurveyFormData;
    questionsForBuyer: Array<SurveyQuestion>;
}) =>
    getDisplayedQuestionsAnswers({
        answers: getAnswersWithoutFrontendOnlyQuestions(formData),
        attendeeSurveys,
        questionsForBuyer,
    });

export const getCleanedFormDataFactory =
    ({
        attendeeSurveys,
        questionsForBuyer,
    }: {
        attendeeSurveys: Array<AttendeeSurvey>;
        questionsForBuyer: Array<SurveyQuestion>;
    }) =>
    (formData: SurveyFormData) => {
        // We must preserve answers that are tied neither to buyer nor attendee (e.g. payment method questions)
        const answersWithoutFrontendOnlyQuestions =
            getAnswersWithoutFrontendOnlyQuestions(formData);

        const answersWithoutHiddenConditionalQuestions =
            getAnswersWithoutHiddenConditionalQuestions({
                attendeeSurveys,
                formData,
                questionsForBuyer,
            });

        return Object.fromEntries(
            Object.entries({
                ...answersWithoutFrontendOnlyQuestions,
                ...answersWithoutHiddenConditionalQuestions,
            }).map(([sectionName, sectionAnswers]) => [
                sectionName,
                getTrimmedStringPropsObject(sectionAnswers),
            ]),
        );
    };
